import React from "react";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import { findProductFromProductId } from "@qgiv/p2p-registration";
import { useFormikContext } from "formik";
import { selectCurrentProductId } from "../../../../redux/slices/productModalSlice";
import { selectAllProducts } from "../../../../redux/slices/productsSlice";
import { selectCmsControlByType } from "../../../../redux/slices/cmsSettingsSlice";
import ProductAttributes from "./ProductAttributes";

const ConnectedProductAttributes = () => {
    const {
        ENUMS: {
            CMS_Control: { P2P_STORE_PRODUCTS },
        },
    } = constants;

    const { values } = useFormikContext();
    const { Field_Data_Id } = values;

    const currentProductId = useSelector(selectCurrentProductId);
    const products = useSelector(selectAllProducts);

    const product = findProductFromProductId(currentProductId, products);
    const { fields, fieldData = {} } = product;

    const control = useSelector((state) =>
        selectCmsControlByType(P2P_STORE_PRODUCTS, state),
    );
    const {
        options: { attributeLabel = "Choose %Attribute%" },
    } = control;

    // -------------------------------------------------------------------------
    // Inventory Related Logic
    // -------------------------------------------------------------------------

    // Filter out only the Attribute Field Values from Formik
    const getAttributeFieldNamesFromValues = () =>
        Object.keys(values).filter(
            // eslint-disable-next-line no-bitwise
            (attributeKey) => ~attributeKey.indexOf("Attribute"),
        );

    const attributeKeysToArray = getAttributeFieldNamesFromValues();

    // Create an array of all the user attribute option selections
    const userAttributeSelection = () => {
        const attributeValues =
            attributeKeysToArray &&
            attributeKeysToArray.map((attribute) => values[attribute]);

        const filteredAttributeValues = attributeValues.filter(
            (el) => el !== "",
        );

        return filteredAttributeValues;
    };

    // Function used to set the initially disabled options
    // These are options that are not available in any combination.
    // Example: There are no longer any Size: Small available in any color or style
    // Small would be added to the initially disabled array and will remain disabled
    // no matter what selections are made by the user
    const determineIfDisabledByInitialInventoryStatus = () => {
        let filteredAttributeOptionDisabled = [];
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const fieldId in fields) {
            const attributeOptionsArray = fields[fieldId].data;
            const attributeOptionHasAvailability =
                fieldData &&
                attributeOptionsArray.map((attributeOptionName) => {
                    const currentAttributeName = fields[fieldId].text;
                    const hasInventory = Object.keys(fieldData).some(
                        (fieldKey) =>
                            fieldData[fieldKey].fields[
                                currentAttributeName
                            ].includes(attributeOptionName),
                    );

                    return !hasInventory ? attributeOptionName : "";
                });
            filteredAttributeOptionDisabled =
                filteredAttributeOptionDisabled.concat(
                    attributeOptionHasAvailability.filter((el) => el !== ""),
                );
        }
        return filteredAttributeOptionDisabled;
    };

    const userMadeAttributeSelection = userAttributeSelection();

    // Function to determine which options are disabled based on user selection.
    // This will return an array of all the attribute options disabled based on current
    // Attribute option selections
    // Example: User selects Large and there are no Blue shirts available in Size: Large.
    // However there are other colors available in Large and Blue is available in other Sizes
    // Blue should be disabled when Large is selected
    const determineIfDisabledByAttributeSelections = () => {
        let filteredAttributeOptionDisabled = [];

        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const fieldId in fields) {
            const attributeOptionsArray = fields[fieldId].data;

            const currentAttributeName = fields[fieldId].text;
            const currentStrippedAlias =
                userMadeAttributeSelection.length > 0
                    ? userMadeAttributeSelection
                          .reduce(
                              (previousValue, currentValue) =>
                                  previousValue + currentValue,
                          )
                          .replace(/\s+/g, "")
                    : "";

            const filteredBySelection = Object.keys(fieldData).filter(
                (fieldKey) =>
                    fieldData[fieldKey].strippedAlias.includes(
                        currentStrippedAlias,
                    ),
            );

            const availableOptions = [];
            // eslint-disable-next-line array-callback-return
            filteredBySelection.map((selected) => {
                const selectedData = fieldData[selected].fields;
                // eslint-disable-next-line array-callback-return, consistent-return
                const allKeys = Object.keys(selectedData).filter((key) => {
                    if (key === currentAttributeName) {
                        return key;
                    }
                });

                allKeys.forEach((element) => {
                    availableOptions.push(selectedData[element]);
                });
            });

            const optionsToDelete = new Set(availableOptions);

            filteredAttributeOptionDisabled = attributeOptionsArray.filter(
                (option) => !optionsToDelete.has(option),
            );
        }

        return filteredAttributeOptionDisabled;
    };

    const productAttributesProps = {
        attributeKeysToArray,
        attributeLabel,
        // Disabled based on other Attribute Selections
        disabledBySelectionList:
            userMadeAttributeSelection.length > 0
                ? determineIfDisabledByAttributeSelections()
                : [],
        // Initially and always disabled
        disabledList: determineIfDisabledByInitialInventoryStatus(),
        fields,
        Field_Data_Id,
    };
    return <ProductAttributes {...productAttributesProps} />;
};

export default ConnectedProductAttributes;
